import { canAccess } from '@react-admin/ra-rbac';
import { useMemo } from 'react';
import {
  DatagridProps,
  Identifier,
  RaRecord,
  usePermissions,
  useResourceDefinition,
} from 'react-admin';

import { Resources } from '@sh4res/bo-common/utils';

type RowClickFn<RecordType extends RaRecord = RaRecord> = (
  id: Identifier,
  resource: Resources,
  record: RecordType,
) => string | false | Promise<string | false>;
export type RowClick<RecordType extends RaRecord = RaRecord> =
  | 'edit'
  | 'show'
  | 'expand'
  | 'toggleSelection'
  | false
  | RowClickFn<RecordType>;

export function useRowClick<RecordType extends RaRecord = RaRecord>(
  rowClick?: RowClick<RecordType>,
) {
  const { permissions } = usePermissions();
  const { name: resourceName } = useResourceDefinition();

  return useMemo<DatagridProps['rowClick']>(() => {
    if (typeof rowClick !== 'string') {
      return rowClick as Exclude<DatagridProps['rowClick'], string>;
    }

    if (rowClick === 'show' || rowClick === 'expand') {
      return canAccess({
        permissions,
        resource: resourceName,
        action: 'show',
      })
        ? rowClick
        : undefined;
    }

    if (rowClick === 'edit') {
      return canAccess({
        permissions,
        resource: resourceName,
        action: 'edit',
      })
        ? rowClick
        : undefined;
    }

    return rowClick;
  }, [rowClick, permissions, resourceName]);
}
